// Angular
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
// Service
import { ObjectUtils } from '@cv-invision/core';
import { ViewsService } from '../../../../../services/views.service';
import { SmartTagsService } from './services/smart-tags.service';
import { LayoutRenderService } from '../../../services/layout-render.service';
import { ScrollableItemsHelperOsService } from '../../../../services/scrollable-items-helper-os.service';
import { CVfUtilsService } from '../../../../services/cvf-utils.service';
// Model
import { SmartTagItem, SmartTagGroup } from './model/smart-tags.model';
import { LayoutComponentBroadcast, LayoutRenderLayoutItem } from '../../../models/cv-layout-render.model';
import { SmartTagsOptions } from '../../models/components.options';
import {
	DropdownButtonItem,
	DROPDOWN_BUTTON_ITEM_TYPE
} from '@cv-invision/cv.component-library.ux';
import { RENDERERS } from './model';
import { ScrollableItemModel } from '../../../../models/extended-section-options';
// Events
import { SmartTagsEventType } from './model/smart-tags.event';
import { GeneralEventType } from '../../models/component.events';

@Component({
	selector: 'cv-schema-render-smart-tags',
	templateUrl: './smart-tags.component.html'
})
export class SmartTagsComponent implements OnInit, OnDestroy, OnChanges {

	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();
	@Output() create: EventEmitter<any> = new EventEmitter<any>();
	@Output() edit: EventEmitter<any> = new EventEmitter<any>();

	options = new SmartTagsOptions();
	path;
	items = [];
	navigateItems: DropdownButtonItem[] = [];
	smartTagsGroups: SmartTagGroup[] = [];
	selectedSmartTagType = null;
	totalSmartTags = 0;
	scrollPosition = 0;
	itemDirectiveData: ScrollableItemModel;
	indexSelectedSmartTagType = -1;
	public smartTagTypeList: any = [];
	counter = 1;
	lastIndex = -1;

	// unused vars
	// photoSelected = false;
	// externalProcess = false;
	// end

	get flattenedPhotos(): any {
		return this.viewsService.flattenedPhotos;
	}

	get order(): any {
		return this.viewsService.order;
	}

	get itemId(): any {
		return `${this.data._Data.section}-${this.data._Data.index.join('-')}`;
	}

	get isEditable(): boolean {
		return this.layoutRenderService.activateEditableUX;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private viewsService: ViewsService,
		private layoutRenderService: LayoutRenderService,
		private smartTagsService: SmartTagsService,
		private scrollableHelper: ScrollableItemsHelperOsService,
		private cvfUtils: CVfUtilsService
	) {
		this.layoutRenderService.componentBroadcast
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: LayoutComponentBroadcast) => {
				if (data.jsonPath === this.data._Path) {
					switch (data.eventType) {
						// it does not run on MapLocation because it is destroyed and recreated.
						case GeneralEventType.DATA_UPDATED:
							this.smartTagsGroups = [];
							this.totalSmartTags = 0;
							this.getSmartTags();

							break;
					}
				}
			});
	}

	ngOnInit(): void {
		this.options = { ...this.options, ...this.layoutNode.options };
		this.itemDirectiveData = this.scrollableHelper.getScrollableItem(this.data._Data.section, this.itemId);
		const smartTagTypeList = ObjectUtils.recursiveDeepCopy(this.layoutRenderService.getExtraDataOption('smartTagTypes')) || [];
		this.smartTagTypeList = this.smartTagsService.getSmartTagTypes(this.data, smartTagTypeList)

		if (this.data) {
			this.totalSmartTags = 0;
			this.smartTagsGroups = [];
			this.path = this.data._Path;
			this.items = ObjectUtils.recursiveDeepCopy(this.data[this.options.key]);
			this.getSmartTags();
		}

		this.smartTagsService.smartTagDataChanged
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				if (event && event.type >= 0 && this.path === event.path) {
					switch (event.type) {
						case SmartTagsEventType.DELETE_SMART_TAG:
							this.deleteSmartTag(event.data);
							break;
					}
				}
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.data && !changes.data.isFirstChange()) {
			this.path = this.data._Path;
			this.totalSmartTags = 0;
			this.smartTagsGroups = [];
			this.items = ObjectUtils.recursiveDeepCopy(this.data[this.options.key]);
		}

		if (changes.layoutNode && !changes.layoutNode.isFirstChange()) {
			this.options = { ...this.options, ...this.layoutNode.options };
		}
	}

	/**
	 * For External Process Only
	 */
	public onCreate(): void {
		if (this.options && this.options.externalProcess) {
			this.create.emit({});
		}
	}

	/**
	 * For External Process Only
	 */
	public onEdit(metadata: any): void {
		if (this.options && this.options.externalProcess) {
			this.edit.emit(metadata);
		}
	}

	/**
	 * parse options data to get smart tag list
	 */
	private getSmartTags(): void {
		this.navigateItems = [];
		this.items = ObjectUtils.recursiveDeepCopy(this.data[this.options.key]);
		if (this.items) {
			if (Array.isArray(this.items)) {
				this.getDataFromArray(this.items);
			} else {
				this.getDataFromObject(this.items);
			}

			this.smartTagsGroups.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
			this.navigateItems.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

			this.smartTagsGroups.forEach((smartTagGroup, index) => {
				smartTagGroup.id = `${this.itemDirectiveData.id}_${index}`;
			})

			if (this.navigateItems[this.indexSelectedSmartTagType]) {
				this.selectedSmartTagType = this.navigateItems[this.indexSelectedSmartTagType].label;
			}
		}
	}

	/**
	 * get data from smart tag Array(4.0)
	 * @param smartTags
	 */
	getDataFromArray(smartTags: any[]): void {
		smartTags.forEach((item: any) => {
			const key = item[this.options.groupKey];
			if ('include' in this.options && Array.isArray(this.options.include) && this.options.include.length > 0) {
				if (this.options.include.indexOf(key) > -1) {
					this.findSmartTagGroup(key, { ...item });
				}
			} else if ('exclude' in this.options && Array.isArray(this.options.exclude) && this.options.exclude.length > 0) {
				if (this.options.exclude.indexOf(key) < 0) {
					this.findSmartTagGroup(key, { ...item });
				}
			} else {
				this.findSmartTagGroup(key, { ...item });
			}
		})
	}

	/**
	 * get data from smart tag Object(3.4)
	 * @param smartTags
	 */
	getDataFromObject(smartTags: any): void {
		Object.keys(smartTags).forEach(groupName => {
			if (this.options && 'groupKey' in this.options) {
				if (Array.isArray(smartTags[groupName])) {
					const items = smartTags[groupName].map(item => ({ ...item, [this.options.groupKey]: groupName }));
					this.getDataFromArray(items);
				}
			}
		});
	}

	/**
	 * Find smart tag section to add or update list of smart tags
	 * @param groupName
	 * @param item
	 */
	findSmartTagGroup(groupName: string, item: any): void {

		const tagType = this.selectSmartTagItem(item.tagType);
		if (tagType) {
			groupName = ('rootParent' in tagType) ? tagType.rootParent.value : tagType.smartTagType?.value;
		}

		const indexSection = this.smartTagsGroups.findIndex((smartTag: SmartTagGroup) => {
			if (groupName && groupName !== null) {
				return smartTag.name.toUpperCase() === groupName.toUpperCase();
			}
			return null;
		});

		if (indexSection > -1) {
			this.updateSmartTagGroup(indexSection, item);
		} else if (groupName !== null) {
			this.addSmartTagGroup(groupName, item);
		}
	}

	/**
	 * Generate a smart tag section and add smart tag item
	 * @param groupName
	 * @param item
	 */
	private addSmartTagGroup(groupName: string, item: any): void {
		const smartTagGroup: SmartTagGroup = {
			name: groupName,
			items: []
		}

		const smartTag = this.getSmartTagData(item);
		if (smartTag) {
			smartTagGroup.items.push(smartTag);
		}

		if (smartTagGroup.items.length > 0) {
			this.smartTagsGroups.push(smartTagGroup);
			this.navigateItems.push({
				label: groupName.toUpperCase() + ` (${smartTagGroup.items.length})`,
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				data: groupName
			});
		}
	}

	/**
	 * Update items in smart tags section
	 * @param index
	 * @param item
	 */
	private updateSmartTagGroup(index: number, item: any): void {
		const smartTag = this.getSmartTagData(item);

		if (smartTag) {
			this.smartTagsGroups[index].items.push(smartTag);

			const indexNavigate = this.navigateItems.findIndex(navigateItems => navigateItems.data === this.smartTagsGroups[index].name);
			if (indexNavigate > -1) {
				this.navigateItems[indexNavigate].label = this.smartTagsGroups[index].name.toUpperCase() + ` (${this.smartTagsGroups[index].items.length})`;
			}
		}
	}

	/**
	 * Select parent from the Event Key
	 * @param eventKey
	 * @private
	 */
	private selectSmartTagItem(eventKey: string, eventValue?: string): any {
		return this.cvfUtils.getSmartTagItem(this.smartTagTypeList, eventKey, eventValue);
	}

	/**
	 * Get data and photos for smart tag item
	 * @param item
	 * @returns
	 */
	private getSmartTagData(item: any): any {
		if (item.hasPhotos) {
			let renderName = null;
			const tagType = this.selectSmartTagItem(item.tagType);

			if (tagType) {
				renderName = this.cvfUtils.getSelectedViewMode(tagType);
			}

			const renderer = new RENDERERS[renderName ? renderName : 'TagDefault'];
			// Create the Render View
			if (renderer.formRender instanceof Function) {
				const formControl = this.cvfUtils.getSelectedFormControls(tagType);
				item.renderView = renderer.formRender(item, formControl, this.options.version);
			} else {
				item.renderView = renderer.render(item, this.options.version);
			}

			let photos = this.getSmartTagPhotos(item);
			photos = photos.map(photo => ({ ...photo, isSmartTag: true, smartTagObjectId: item.smartTagObjectId }));
			const smartTag: SmartTagItem = {
				data: { ...item },
				photos: photos
			}

			this.totalSmartTags++;

			return smartTag;
		}
	}

	/**
	 * get smart tag photos with path data
	 * @param item
	 * @returns
	 */
	private getSmartTagPhotos(item: any): any[] {
		let photos = [];
		const fdPhotos = this.flattenedPhotos[item._Path] || [];

		if (fdPhotos.length) {
			photos = photos.concat(fdPhotos[0]);
		}

		return photos;
	}

	/**
	 * Rename function
	 * Event to scroll top in specific smart tag group
	 * @param $event
	 */
	scrollToSmartTagGroup($event): void {
		const index = $event.index;
		this.indexSelectedSmartTagType = index;
		const selector = `${this.itemDirectiveData.id}_${this.indexSelectedSmartTagType}`;
		const element = document.getElementById(selector);
		const elementPosition = element.offsetTop;
		this.selectedSmartTagType = $event.item.label;

		if (index !== this.lastIndex) {
			this.lastIndex = index;
			this.scrollPosition = elementPosition - 30;
		} else {
			this.counter++;

			if (this.counter > 1) {
				this.scrollPosition = elementPosition - (29);
				this.counter = 0;
			} else {
				this.scrollPosition = elementPosition - 30;
			}
		}
	}

	/**
	 * event to delete smart tag from list
	 * @param item
	 */
	private async deleteSmartTag(item: any): Promise<void> {
		const smartTagPhoto = item.photos[0];
		if ((smartTagPhoto && smartTagPhoto.isSmartTag) || item.data.smartTagObjectId != null) {

			const smartTagDataIndex = this.smartTagsGroups.findIndex((smartTagGroup: any) => {
				return smartTagGroup.name === item.data[this.options.groupKey];
			});

			let smartTagItemIndex = -1;

			if (smartTagDataIndex >= 0) {
				smartTagItemIndex = this.smartTagsGroups[smartTagDataIndex].items.findIndex((smartTagItem: any) => {
					return smartTagItem.data.smartTagObjectId === item.data.smartTagObjectId;
				});
			}

			if (smartTagItemIndex >= 0) {
				this.smartTagsGroups[smartTagDataIndex].items.splice(smartTagItemIndex, 1);
				if (this.smartTagsGroups[smartTagDataIndex].items.length <= 0) {
					this.smartTagsGroups.splice(smartTagDataIndex, 1);
				}
			}

			let broadcastMetadata;

			if (smartTagPhoto) {
				broadcastMetadata = await this.smartTagsService.deleteSmartTag(smartTagPhoto, 'v2');
			} else {
				const smartTagData = { smartTagObjectId: item.data.smartTagObjectId, refJsonPath: item.data._Path };
				broadcastMetadata = await this.smartTagsService.deleteSmartTag(smartTagData, 'v2');
			}

			if (broadcastMetadata && broadcastMetadata.path) {
				this.layoutRenderService.componentBroadcast.next({
					eventType: GeneralEventType.UPDATE_DATA,
					jsonPath: this.path,
					metaData: broadcastMetadata
				});
			}
		}
	}

	/**
	 * Close all when the element is destroyed
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
